import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import { PaymentRateParams } from '@nxsys/store/slices/paymentSetting/types';
import { makeCode } from '@nxsys/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PaymentRateForm, {
  PaymentRateFormRef,
  PaymentRateFormValues,
} from '../components/PaymentRateForm';
import AppLoader from '@nxsys/components/AppLoader';
import { StatusEnum } from '@nxsys/constants/AppEnums';

type CreatePaymentRateProps = {
  isOpen: boolean;
  toggle: () => void;
  refetchData: () => void;
};

const CreatePaymentRateModal: React.FC<CreatePaymentRateProps> = ({
  isOpen,
  toggle,
  refetchData,
}: CreatePaymentRateProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<PaymentRateFormRef>(null);

  const defaultCode = useMemo(() => makeCode('PR', 3), []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { vatCodeList } = useSelector(
    (state: AppState) => state.paymentSettingStore,
  );
  const { list: vatCodeListData, loading: vatCodeListLoading } = vatCodeList;

  useEffect(() => {
    dispatch(
      paymentSettingActions.getVATCodeList({
        page: 0,
        size: 20,
      }),
    );
  }, []);

  const handleSubmit = (values: PaymentRateFormValues) => {
    setIsSubmitting(true);
    let params: PaymentRateParams;
    // NX-5228: country decides the payload shape, so it is tested first.
    // Checking `!isBureau` first sent an umbrella-Ireland edit the UK-shaped
    // flags and dropped PRSI/USC/ASC/LPT and includeInPayeCalculation.
    if (isIreland) {
      params = {
        code: values.code ?? defaultCode,
        description: values.description ?? '',
        status: values?.status ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
        hoursPerUnit: values?.hoursPerUnit ?? '',
        defaultVATCode: values.defaultVATCode ?? '',
        taxCanBeDeducted: Boolean(values.taxCanBeDeducted),
        employeePRSICanBeDeducted: Boolean(values.employeePRSICanBeDeducted),
        employerPRSICanBeDeducted: Boolean(values.employerPRSICanBeDeducted),
        uscCanBeDeducted: Boolean(values.uscCanBeDeducted),
        ascCanBeDeducted: Boolean(values.ascCanBeDeducted),
        pensionSchemaDeductionCanBeDeducted: Boolean(
          values.pensionSchemaDeductionCanBeDeducted,
        ),
        lptCanBeDeducted: Boolean(values.lptCanBeDeducted),
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        includeInPayeCalculation: true,
      };
    } else if (!isBureau) {
      params = {
        code: values.code ?? defaultCode,
        defaultVATCode: values.defaultVATCode ?? '',
        description: values.description ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        hoursPerUnit: values?.hoursPerUnit ?? '',
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        includeNICalculation: Boolean(values.includeNICalculation),
        includePensionCalculation: Boolean(values.includePensionCalculation),
        includeTaxCalculation: Boolean(values.includeTaxCalculation),
        raiseInvoice: Boolean(values.raiseInvoice),
        status: values?.status ?? '',
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
      };
    } else {
      params = {
        code: values.code ?? defaultCode,
        defaultVATCode: values.defaultVATCode ?? '',
        description: values.description ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        hoursPerUnit: values?.hoursPerUnit ?? '',
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeNICalculation: Boolean(values.includeNICalculation),
        includePensionCalculation: Boolean(values.includePensionCalculation),
        includeTaxCalculation: Boolean(values.includeTaxCalculation),
        raiseInvoice: true,
        status: values?.status ?? '',
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
        includeInCisCalculation: false,
        includeInPayeCalculation: true,
      };
    }
    dispatch(paymentSettingActions.createPaymentRate(params))
      .then(() => {
        setIsSubmitting(false);
        toggle();
        refetchData();
      })
      .catch((error) => {
        console.error(error);
        setIsSubmitting(false);
      });
  };

  return (
    <AppModalContainer
      title={messages['paymentSetting.paymentRate.addNewPaymentRate']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {}
      {(vatCodeListLoading || isSubmitting) && <AppLoader />}
      <PaymentRateForm
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={{
          code: defaultCode,
          defaultVATCode: vatCodeListData[0]?.id,
          displayOnPayslip: true,
          status: StatusEnum.ACTIVE,
          // NX-5228: the default set is country-shaped, not product-shaped.
          ...(!isIreland
            ? {
                includeAccrueHolidayPay: true,
                excludeFromNmwCalculation: false,
                includeInCisCalculation: !isBureau ? true : false,
                includeNICalculation: true,
                includePensionCalculation: true,
                includeTaxCalculation: true,
                raiseInvoice: true,
              }
            : {
                taxCanBeDeducted: true,
                employeePRSICanBeDeducted: true,
                employerPRSICanBeDeducted: true,
                uscCanBeDeducted: true,
                ascCanBeDeducted: true,
                pensionSchemaDeductionCanBeDeducted: true,
                lptCanBeDeducted: true,
                includeAccrueHolidayPay: true,
                excludeFromNmwCalculation: false,
                includeInCisCalculation: false,
              }),
        }}
      />
    </AppModalContainer>
  );
};

export default CreatePaymentRateModal;
